import React from "react";
import PropTypes from "prop-types";
import { LinkConnector } from "../LinkConnector";
import { RCL as useTranslation } from "../../components/RCL";
import { GridCollection, PaginatedCollection, SliderCollection } from "@sunwing/wvi-components";
import { FluidSections } from "../FluidSections";

const paginationVariantAdapter = variant => {
	const variantMap = {
		Default: "default",
		Extended: "extended",
		"No Controls": "no-controls",
	};
	return variant in variantMap ? variantMap[variant] : variant;
};

const themeAdapter = theme => {
	const themeMap = {
		Dark: "dark",
		Light: "light",
	};
	return theme in themeMap ? themeMap[theme] : theme;
};

const navigationAdapter = data => {
	const navigationMap = {
		Display: "display",
		Hide: "hide",
		"Desktop Only": "desktopOnly",
	};
	return data in navigationMap ? navigationMap[data] : data;
};

const paginationAdapter = data => {
	const paginationMap = {
		Display: "display",
		Hide: "hide",
		"Desktop Only": "desktopOnly",
	};
	return data in paginationMap ? paginationMap[data] : data;
};

const CollectionConnector = ({ data }) => {
	const loadAllLabel = useTranslation({ searchKey: "load-all" });
	const loadMoreLabel = useTranslation({ searchKey: "load-more" });

	const defaultPaginationLabels = {
		// TODO: Add new RCL key
		first: useTranslation({ searchKey: "WVI-pagination-first" }),
		last: useTranslation({ searchKey: "WVI-pagination-last" }),
		back: useTranslation({ searchKey: "WVI-pagination-back" }),
		next: useTranslation({ searchKey: "WVI-pagination-next" }),
		of: useTranslation({ searchKey: "WVI-pagination-of" }),
	};

	if (!data?.cards?.length) {
		return null;
	}
	switch (data.variant) {
		case "paginated":
			return (
				<PaginatedCollection
					// TODO: Localize
					id={data.id}
					paginationVariant={paginationVariantAdapter(data.paginationVariant)}
					labels={defaultPaginationLabels}
					numberOfPaginationToDisplay={data.numberOfPaginationToDisplay}
					theme={themeAdapter(data.theme)}
					cardsPerPage={data.cardsPerPage}
					items={data.cards
						.filter(x => x)
						.sort((a, b) => {
							if (data.sortAlphabetically) {
								return a?.heading?.localeCompare(b?.heading);
							}
							return 0;
						})
						.map(item => ({
							id: item.id,
							content: <FluidSections data={[item]} useWrapper={false} />,
						}))}
				/>
			);
		case "slider":
			return (
				<SliderCollection
					id={data.id}
					labels={{
						loadAll: loadAllLabel,
						loadMore: loadMoreLabel,
					}}
					slidesPerView={data.itemsPerRow}
					navigationType={navigationAdapter(data.navigationType)}
					paginationType={paginationAdapter(data.paginationType)}
					theme={themeAdapter(data.theme)}
					slides={data.cards
						.filter(x => x)
						.sort((a, b) => {
							if (data.sortAlphabetically) {
								return a?.heading?.localeCompare(b?.heading);
							}
							return 0;
						})
						.map(item => ({
							id: item.id,
							view: <FluidSections key={item.id} data={[item]} useWrapper={false} />,
						}))}
					link={
						!!data.link && {
							href: data.link?.url,
							text: data.link?.linkText,
							target: data.link?.target,
						}
					}
					renderLink={(href, { children, ...props }) => (
						// eslint-disable-next-line react/jsx-props-no-spreading
						<LinkConnector to={href} {...props}>
							{children}
						</LinkConnector>
					)}
				/>
			);
		case "grid":
		default:
			return (
				<GridCollection
					id={data.id}
					itemsPerRow={data.itemsPerRow}
					items={data.cards
						.filter(x => x)
						.sort((a, b) => {
							if (data.sortAlphabetically) {
								return a?.heading?.localeCompare(b?.heading);
							}
							return 0;
						})
						.map(item => ({
							id: item.id,
							content: <FluidSections data={[item]} useWrapper={false} />,
						}))}
				/>
			);
	}
};

CollectionConnector.propTypes = {
	data: PropTypes.shape({
		cards: PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.string,
				heading: PropTypes.string,
				headerAs: PropTypes.string,
				subheading: PropTypes.string,
				variant: PropTypes.oneOf([
					"Primary Default",
					"Primary Even Split",
					"Primary Logo Offset",
					"Info Default",
					"Info Horizontal",
					"Info Horizontal No Icon",
					"Top Tile Default",
					"Top Tile Compact",
				]).isRequired,
				content: PropTypes.shape({
					raw: PropTypes.string,
				}),
				image: PropTypes.arrayOf(
					PropTypes.shape({
						public_id: PropTypes.string,
						secure_url: PropTypes.string,
					})
				),
				icon: PropTypes.string,
				link: PropTypes.shape({
					variant: PropTypes.oneOf([
						"Primary",
						"Primary (Outline)",
						"Primary (Inverse)",
						"Primary (Outline and Inverse)",
						"Action",
						"Action (Outline)",
						"Action (Inverse)",
						"Action (Outline and Inverse)",
						"Utility",
					]).isRequired,
					size: PropTypes.oneOf(["sm", "md", "lg"]),
					linkText: PropTypes.string,
					url: PropTypes.string.isRequired,
					target: PropTypes.oneOf(["_blank", "_self", "_parent", "_top", "EXTERNAL_MODAL"])
						.isRequired,
					image: PropTypes.arrayOf(
						PropTypes.shape({
							public_id: PropTypes.string,
							secure_url: PropTypes.string,
						})
					),
					imageAltText: PropTypes.string,
					iconName: PropTypes.string,
					type: PropTypes.oneOf(["link", "button"]),
					align: PropTypes.oneOf(["left", "center", "right"]),
					modal: PropTypes.arrayOf(PropTypes.object),
				}),
				logo: PropTypes.arrayOf(
					PropTypes.shape({
						public_id: PropTypes.string,
						secure_url: PropTypes.string,
					})
				),
				fromPrice: PropTypes.number,
				toPrice: PropTypes.number,
				priceMessage: PropTypes.string,
			}).isRequired
		).isRequired,
		variant: PropTypes.oneOf(["paginated", "grid", "slider"]).isRequired,
		sortAlphabetically: PropTypes.bool,
		link: PropTypes.shape({
			variant: PropTypes.oneOf([
				"Primary",
				"Primary (Outline)",
				"Primary (Inverse)",
				"Primary (Outline and Inverse)",
				"Action",
				"Action (Outline)",
				"Action (Inverse)",
				"Action (Outline and Inverse)",
				"Utility",
			]).isRequired,
			size: PropTypes.oneOf(["sm", "md", "lg"]),
			linkText: PropTypes.string,
			url: PropTypes.string.isRequired,
			target: PropTypes.oneOf(["_blank", "_self", "_parent", "_top", "EXTERNAL_MODAL"]).isRequired,
			image: PropTypes.arrayOf(
				PropTypes.shape({
					public_id: PropTypes.string,
					secure_url: PropTypes.string,
				})
			),
			imageAltText: PropTypes.string,
			iconName: PropTypes.string,
			type: PropTypes.oneOf(["link", "button"]),
			align: PropTypes.oneOf(["left", "center", "right"]),
			modal: PropTypes.arrayOf(PropTypes.object),
		}),
		cardsPerPage: PropTypes.number.isRequired,
		itemsPerRow: PropTypes.oneOf([1, 2, 3, 4, 5, 6]).isRequired,
		navigationType: PropTypes.oneOf(["Display", "Hide", "Desktop Only"]).isRequired,
		paginationType: PropTypes.oneOf(["Display", "Hide", "Desktop Only"]).isRequired,
		title: PropTypes.string,
		id: PropTypes.string,
		theme: PropTypes.string,
		paginationVariant: PropTypes.oneOf(["Default", "Extended", "No Control"]),
		numberOfPaginationToDisplay: PropTypes.number,
	}).isRequired,
};

export { CollectionConnector };
